import { Layout, notification } from "antd"
import { withRouter, StaticContext, RouteComponentProps } from "react-router"
import usePlatform from "contexts/platform/use-platform"
import { ICreatePolicyholder } from "platform-client/client/controllers/policyholders"
import React from "react"
import { useMutation, useQueryClient } from "react-query"
import ClientForm, { IClientData } from "components/Client/ClientForm"
import { LayoutPageContent } from "components"
import { AxiosError } from "axios"
import { IValidationErrorModel } from "platform-client/types/tobes"

const CreateClient: React.FC<ICreateClientProps> = (props) => {
  const queryClient = useQueryClient()

  const goBack = () => {
    const params = new URLSearchParams(props.location.search)

    if (params.has("url")) {
      const url = params.get("url") || ""
      return props.history.push(url)
    }

    return props.history.push("/clients")
  }

  const platform = usePlatform()

  const createPolicyholder = useMutation(
    (createPolicyholderRequest: ICreatePolicyholder) =>
      platform.policyholders.createPolicyholder(createPolicyholderRequest)
  )

  const onFinish = async (formData: IClientData) => {
    if (formData.policyholderType) {
      // Construct legalName from name fields for Individual, or use businessName for Business
      let legalName: string
      if (formData.policyholderType === "Business") {
        legalName = formData.businessName || ""
      } else {
        const nameParts = [
          formData.title,
          formData.firstName,
          formData.lastName
        ].filter(Boolean)
        legalName = nameParts.join(" ").trim()
      }

      if (!legalName) {
        notification.error({
          message: "Unable to submit form, missing required fields."
        })
        return
      }

      createPolicyholder
        .mutateAsync({
          businessName: formData.businessName || null,
          contact: {
            name: {
              firstName: formData.firstName || null,
              lastName: formData.lastName || null,
              title: formData.title || null
            },
            dateOfBirth: formData.dateOfBirth
              ? formData.dateOfBirth.format("YYYY-MM-DD")
              : null,
            phoneNumber: formData.phoneNumber || null,
            emailAddress: formData.emailAddress || null
          },
          contactAddress: {
            line1: formData?.contactAddress?.line1 || null,
            line2: formData?.contactAddress?.line2 || null,
            line3: formData?.contactAddress?.line3 || null,
            locality: formData?.contactAddress?.locality || null,
            province: formData?.contactAddress?.province || null,
            postalCode: formData?.contactAddress?.postalCode || null,
            country: formData?.contactAddress?.country
              ? JSON.parse(formData?.contactAddress?.country)
              : { referenceID: null, text: null },
            provinceReferenceID:
              formData?.contactAddress?.provinceReferenceID ?? null,
            uniqueReference: formData?.contactAddress?.uniqueReference ?? null
          },
          legalName: legalName,
          policyholderType: formData.policyholderType,
          ownerID: formData.ownerID || null,
          companyID: formData.companyID || null,
          referenceID: formData.referenceID || null
        })
        .then((response) => {
          queryClient.invalidateQueries(["listPolicyholders", "Policyholder"])
          queryClient.refetchQueries(["listPolicyholders", "Policyholder"])
          props.history.push(`/client/${response.id}`)
        })
        .catch((error: AxiosError) => {
          if (error.response?.data?.validationErrors) {
            notification.error({
              message: error.response?.data?.validationErrors
                .map((error: IValidationErrorModel) => error.message)
                .join(", ")
            })
          } else {
            notification.error({
              message: "Unable to create client, please try again."
            })
          }
        })
    } else {
      notification.error({
        message: "Unable to submit form, missing required fields."
      })
    }
  }

  return (
    <Layout>
      <LayoutPageContent>
        <ClientForm
          onFinish={onFinish}
          isLoading={createPolicyholder.isLoading}
          onBack={goBack}
        />
      </LayoutPageContent>
    </Layout>
  )
}

type ICreateClientProps = RouteComponentProps<
  Record<string, never>,
  StaticContext
>

export default withRouter(CreateClient)
